import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import {PropSheet, PropSheetTitle, TextField, OptionSelect, CheckboxField, SmallButton } from './UI';
import { sizeChoices, styleChoices, fontSizeChoices } from '../../modules/defines/style';
import { Parts } from './elements/Parts';
import { PatternBox } from './elements/PatternBox';
import { PhraseBoard } from './elements/PhraseBoard';
import { LinkBlock } from './elements/LinkBlock';
import { Text } from './elements/Text';
import { GraphicUpload } from './elements/GraphicUpload';
import Button from '@material-ui/core/Button';
import EditorContext from '../../modules/contexts/editor';
import styled from "styled-components/macro";
import DeleteIcon from '@material-ui/icons/DeleteForeverOutlined';
import ShowIcon from '@material-ui/icons/VisibilityOffOutlined';
import HideIcon from '@material-ui/icons/VisibilityOutlined';
import DuplicateIcon from '@material-ui/icons/FileCopyOutlined';
import MoveUpIcon from '@material-ui/icons/ExpandLessOutlined'; // ArrowUpwardOutlined';
import MoveDownIcon from '@material-ui/icons/ExpandMoreOutlined'; // ArrowDownwardOutlined';
import deepClone from 'deep-clone';
import { setAnimStep } from '@mirinae/lessons/editor/Editor';
import { List } from './elements/List';
import PatternAnimation from '../../../components/lesson/elements/PatternAnimation';
import { getElementComponent} from '../../../components/lesson/elementComponentMap';
import _ from 'lodash';

const { escapeRegExp } = _;

const DISABLE_FOR_NOW = true;

const FieldWrapper = styled.div`
    // background-color: ${props => (props.index % 2 === 0 ? 'white' : '#f3f3f3')};
    margin-bottom: ${props => (props.open ? '10px' : '0px')};
`;

const ExpandingFieldTitle = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: bold;
    // margin: 0 5px 6px 0;
`;

const Tools = styled.span`
`;

const ToolButton = styled.span`
    cursor: ${props => (props.disable ? 'inherit' : 'pointer')};
    padding: 0px 6px;
    margin: 2px 0px;
    color: ${props => (props.disable ? '#bbb' : '#6a6a6a')};
`;

const FieldTitle = styled.input`
    align-items: center;
    font-size: 12px;
    font-weight: bold;
    border: none;
    width: 225px;
`;

const OpenClose = styled.span`
    font-family: Webdings;
    font-size: 18px;
    cursor: pointer;
`;

const SRWrapper = styled.div`
    position: relative;
`;

const SRPopup = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    background: white;
    top: ${props => props.top}px;
    left: 10px;
    border: thin solid lightgray;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
    width: 300px;
    padding: 6px 15px;
    z-index: 1000;
`;

const SRPopupButttons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

export const SearchAndReplace = ({ node, srState, mouseCoords }) => {
    const editor = useContext(EditorContext);
    const [show, setShow] = srState;
    const [haveIput, setHaveInput] = useState(false);
    const findRef = useRef(null);
    const replRef = useRef(null);
    const srwRef = useRef(null);

    useEffect(() => {
        if (show && findRef.current)
            findRef.current.focus();
    }, [show]);

    const handleReplace = () => {
        const replPart = (f, pn, i, p) => {
            if (p.value.indexOf(findRef.current.value) >= 0) {
                const newValue = p.value.replace(new RegExp(escapeRegExp(findRef.current.value), 'g'), replRef.current.value);
                editor.updateElementFieldPart(node, f, pn, i, 'value', newValue);
            }
            if (p.command && p.command.indexOf(findRef.current.value) >= 0) {
                const newCommand = p.command.replace(new RegExp(escapeRegExp(findRef.current.value), 'g'), replRef.current.value);
                editor.updateElementFieldPart(node, f, pn, i, 'command', newCommand);
            }
        };
        node.fields.forEach(f => {
            f.parts && Object.getOwnPropertyNames(f.parts).forEach(pn => {
                f.parts[pn].forEach((p, i) => replPart(f, pn, i, p));
            });
        });
        editor.commitNodeUpdate(node);
        setShow(false);
    };

    return (
        <SRWrapper ref={srwRef}>
            <SmallButton variant="outlined" onClick={() => setShow(true)}>Search and Replace</SmallButton>
            { show && (
                <SRPopup top={mouseCoords.current.y - srwRef.current.getBoundingClientRect().top}>
                    <TextField inputRef={findRef} onChange={() => setHaveInput(true)} label="Find" />
                    <TextField inputRef={replRef} onChange={() => setHaveInput(true)}  label="Replace" />
                    <SRPopupButttons>
                        <Button variant="outlined" onClick={() => setShow(false)}>Cancel</Button>
                        <Button variant="outlined" onClick={handleReplace} disabled={!haveIput}>Replace all</Button>
                    </SRPopupButttons>
                </SRPopup>
            )}
        </SRWrapper>
    )
};

const ElementPropSheet = ({ node }) => {
    const editor = useContext(EditorContext);
    const [showSearchAndReplace, setShowSearchAndReplace] = useState(false);
    const [draw, setDraw] = useState(1);
    const redraw = () => setDraw(draw + 1);
    const mouseCoords = useRef({ x: 0, y: 0 });

    const isPatternAnim = useMemo(() => getElementComponent(node) === PatternAnimation);
    const animExample = useMemo(() => {
        const ef = isPatternAnim && node.fields.find(f => f.example || f.name.toLowerCase() === 'example');
        if (ef) { // cleanup hack to condition the Example field in a ProcessExamples page
            ef.example = true;
            ef.collapsed = false;
            ['koreanParts', 'englishParts'].forEach(l => ef.parts[l] = ef.parts[l].filter(p => !p.color.match(/merge|separate|separator|addition/)));
        }
        return ef;
    });

    const handleKeyUp = (e) => {
        if (e.metaKey || e.ctrlKey) {
            if (e.key === 'f') {
                e.preventDefault();
                e.stopPropagation();
                setShowSearchAndReplace(true);
            }
        }
    };
    const handleMouseMove = (e) => mouseCoords.current = { x: e.clientX, y: e.clientY };

    useEffect(() => {
        // disaple auto-player update for PatternAnimation editing
        if (isPatternAnim) {
            editor.setUpdatePlayerOnEdit(false);
            // editor.update({ animationEditor: { show: true, element: node }});
        }

        document.addEventListener('keydown', handleKeyUp);
        document.addEventListener('mousemove', handleMouseMove);

        return () => {
            document.removeEventListener('keydown', handleKeyUp);
            document.removeEventListener('mousemove', handleKeyUp);
            if (setAnimStep) setAnimStep('');
            editor.setUpdatePlayerOnEdit(true);
            editor.update({ animationEditor: { show: false, element: null }});
        };
    }, [node]);

    const onBlur = (e, field, value) => {
        editor.updateElementField(node, field, 'value', value !== undefined ? value : e.target.value);
        editor.commitNodeUpdate(node);
    };

    const onCheckboxChange = (e, field, prop) => {
        editor.updateElementField(node, field, prop , e.target.checked);
        editor.commitNodeUpdate(node);
    };

    const onElementFieldPropChanged = (field, prop, value) => {
        editor.updateElementField(node, field, prop , value);
        editor.commitNodeUpdate(node);
    };

    const onNodeFieldBlur = (e, field, value) => {
        editor.updateNodeField(node, field, value !== undefined ? value : e.target.value);
        editor.commitNodeUpdate(node);
    };

    const fieldEdit = (edit) => node.fieldEdits && node.fieldEdits.includes(edit);

    const moveDown = (f) => {
        if (f.index < node.fields.length - 1) {
            editor.editElementFields(node, 'moveDown', f);
            node.fields.splice(f.index, 1);
            node.fields.splice(f.index + 1, 0, f);
            node.fields.forEach((f, i) => f.index = i);
            editor.commitNodeUpdate(node);
        }
    };
    const moveUp = (f) => {
        if (f.index > 0) {
            editor.editElementFields(node, 'moveUp', f);
            node.fields.splice(f.index, 1);
            node.fields.splice(f.index - 1, 0, f);
            node.fields.forEach((f, i) => f.index = i);
            editor.commitNodeUpdate(node);
        }
    };
    const duplicate = (f) => {
        const copy = { ...deepClone(f), name: `${f.name}-copy`, label: `${f.label}-copy`};
        editor.editElementFields(node, 'duplicate', f);
        node.fields.splice(f.index + 1, 0, copy);
        node.fields.forEach((f, i) => f.index = i);
        editor.commitNodeUpdate(node);
    };
    const showHide = (f) => {
        editor.updateElementField(node, f, 'hidden' , !f.hidden);
        editor.commitNodeUpdate(node);
    };
    const del = (f) => {
        editor.editElementFields(node, 'del', f);
        node.fields.splice(f.index, 1);
        node.fields.forEach((f, i) => f.index = i);
        editor.commitNodeUpdate(node);
     };

    const fieldTitleBlur = (e, f) => {
        if (fieldEdit('rename')) {
            const newLabel = e.target.value;
            if (f.states) {
                // for now force step states to be a singleton state named for any label change
                editor.updateElementField(node, f, 'states', [newLabel]);
            }
            delete node.fieldsByName[f.name]; node.fieldsByName[newLabel] = f;
            editor.updateElementField(node, f, 'label', newLabel);
            if (!f.name.match(/Example/i))  // don't allow rename of examples field for patternAnimations todo: this should be a field property in lessonDefs!
                editor.updateElementField(node, f, 'name', newLabel);
            editor.commitNodeUpdate(node);
        }
    };

    return draw && (
        <PropSheet>
            <PropSheetTitle>{`${node.title} - ${node.type} (${node.nodeID})`}</PropSheetTitle>
            <TextField label='Element title' inputRef={input => { if (input) input.value = node.title; }} onBlur={e => onNodeFieldBlur(e, 'title')}  />
            { isPatternAnim && (
                <>
                    { node.fieldsByName.layout && <OptionSelect node={node} field={node.fieldsByName.layout} onSelect={(e) => onBlur(e, node.fieldsByName.layout)} style={{ justifyContent: 'center' }}/> }
                    <Button variant="outlined" size="small" style={{ margin: '6px 110px 12px 110px' }} onClick={() => editor.update({ animationEditor: { show: true, element: node }})}>
                        Open animation editor
                    </Button>
                </>
            )}
            { fieldEdit('searchAndReplace') && !isPatternAnim && <SearchAndReplace node={node} srState={[showSearchAndReplace, setShowSearchAndReplace]} mouseCoords={mouseCoords} /> }
            { node.fields && node.fields.filter(f => !isPatternAnim || f.example).map((f, i) => (
                <FieldWrapper key={`${node.nodeID}-${i}`}  ref={ref => f.ref = ref} index={i} open={f.parts && !f.collapsed}>
                    { f.parts && !f.example && (
                        <ExpandingFieldTitle>
                            <span>
                                <OpenClose open={!f.collapsed} onClick={() => { f.collapsed = !Boolean(f.collapsed); redraw(); }}>{f.collapsed ? '' : ''}</OpenClose>
                                <FieldTitle
                                    readOnly={!fieldEdit('rename')} ref={input => { if (input) input.value = f.label || f.name; }}
                                    onChange={() => {}} onBlur={e => fieldTitleBlur(e, f)} />
                            </span>
                            <Tools>
                                { fieldEdit('move') && <ToolButton disable={f.index === node.fields.length - 1} onClick={() => moveDown(f)}><MoveDownIcon /></ToolButton> }
                                { fieldEdit('duplicate') && <ToolButton onClick={() => duplicate(f)}><DuplicateIcon /></ToolButton> }
                                { fieldEdit('hide') && <ToolButton onClick={() => showHide(f)}>{f.hidden ? <ShowIcon /> : <HideIcon />}</ToolButton> }
                                { fieldEdit('delete') && <ToolButton onClick={() => del(f)}><DeleteIcon /></ToolButton> }
                                { fieldEdit('move') && <ToolButton disable={f.index === 0} onClick={() => moveUp(f)}><MoveUpIcon /></ToolButton> }
                                {/* <CheckboxField label="Hide" style={{ display: 'inline' }} checked={f.hidden || false} onChange={e => onCheckboxChange(e, f, 'hidden')} /> */}
                            </Tools>
                        </ExpandingFieldTitle>
                    )}
                    { f.example &&
                        <FieldTitle readOnly ref={input => { if (input) input.value = f.label || f.name; }} onChange={() => {}} onBlur={e => fieldTitleBlur(e, f)} />
                    }
                    { !f.collapsed && (
                          (f.type && f.type.match(/parts|underlinedPart|shortenedParts/) ? <Parts key={i} node={node} field={f} />
                        : (f.type === 'pattern') ? <PatternBox key={i} node={node} field={f} />
                        : (f.type === 'phraseBoard' ? <PhraseBoard key={i} node={node} field={f} />
                        : (f.type === 'linkBlock' ? <LinkBlock key={i} node={node} field={f} />
                        : (f.type === undefined || f.type.match(/text/i) ? <Text key={i} node={node} field={f} />
                        : (f.type === 'choice' ? f.choices && <OptionSelect key={i} node={node} field={f} onSelect={(e) => onBlur(e, f)} />
                        : (f.type === 'graphic' ? <GraphicUpload key={i} node={node} field={f} />
                        : (f.type === 'list' && <List key={i} node={node} field={f} />))))))))
                    }
                </FieldWrapper>
            ))}
            { node.fields && node.fields.length === 1 && node.fields[0].type === 'text' && (
                <OptionSelect node={node} field={{label: 'Font size', value: node.fontSize || 'default', choices: fontSizeChoices }} onSelect={e => onNodeFieldBlur(e, 'fontSize')} />
            )}
            { !DISABLE_FOR_NOW && node.size && (
                <OptionSelect node={node} field={{label: 'Size', value: node.size, choices: sizeChoices(node)}} onSelect={e => onNodeFieldBlur(e, 'size')} />
            )}
            { !DISABLE_FOR_NOW && styleChoices(node) && (
                <OptionSelect node={node} field={{label: 'Style', value: node.style || 'Normal', choices: styleChoices(node)}} onSelect={e => onNodeFieldBlur(e, 'style')} />
            )}
        </PropSheet>
    );
};

export default ElementPropSheet;
